import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Icon from './Icon';
import prospectService from '../services/prospectService';

/**
 * Everything Orion knows about one person, and the only place it can be fixed.
 *
 * An import gets things wrong — a company name mangled by an export, a missing
 * address somebody has since found on a website — and until now the record was
 * whatever the file said, permanently. Every field here saves on blur, the way
 * project Settings does, because a drawer with a Save button is a drawer people
 * close without pressing it.
 *
 * Email status is deliberately not editable. It is the system's judgement about
 * whether an address will deliver and the send path trusts it; a text box that
 * could write "confirmed" would make that judgement worthless everywhere. An
 * address typed here is recorded as added by you, so the provenance stays
 * visible rather than being passed off as a lookup that never happened.
 */

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(4, 6, 14, 0.55);
  z-index: 900;
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(460px, 100vw);
  background: var(--surface-1);
  border-left: 1px solid var(--border-color);
  z-index: 901;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-lg);
`;

const Head = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.15rem 1.35rem 1rem;
  border-bottom: 1px solid var(--border-color);
`;

const Name = styled.h2`
  font-family: var(--font-heading);
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.15rem;
  line-height: 1.25;
`;

const Sub = styled.div`
  color: var(--text-secondary);
  font-size: var(--text-sm);
`;

const IconButton = styled.button`
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: var(--radius-md);
  border: 1px solid transparent;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;

  &:hover { color: var(--text-primary); background: var(--surface-hover); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.4rem 1.35rem 1.5rem;
`;

const Section = styled.section`
  padding: 1.1rem 0;
  border-bottom: 1px solid var(--border-color);

  &:last-of-type { border-bottom: none; }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 0.75rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 6.5rem minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  padding: 0.22rem 0;
`;

const Key = styled.label`
  color: var(--text-muted);
  font-size: var(--text-sm);
`;

/**
 * A value that turns into an input when you click it.
 *
 * Rendering every field as a permanent text box would make a read-only glance —
 * the common case — look like a form somebody abandoned half-filled.
 */
const Value = styled.button`
  width: 100%;
  text-align: left;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  padding: 0.32rem 0.45rem;
  margin-left: -0.45rem;
  color: ${(p) => (p.$empty ? 'var(--text-muted)' : 'var(--text-primary)')};
  font-family: inherit;
  font-size: var(--text-base);
  cursor: text;
  overflow-wrap: anywhere;

  &:hover { border-color: var(--border-color); background: var(--surface-hover); }
`;

const Input = styled.input`
  width: 100%;
  background: var(--surface-3);
  border: 1px solid var(--border-accent);
  border-radius: var(--radius-sm);
  padding: 0.32rem 0.45rem;
  margin-left: -0.45rem;
  color: var(--text-primary);
  font-family: inherit;
  font-size: var(--text-base);

  &:focus { outline: none; }
`;

const Static = styled.div`
  color: var(--text-primary);
  font-size: var(--text-base);
  padding: 0.32rem 0;
  overflow-wrap: anywhere;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: var(--text-xs);
  font-weight: 600;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  border: 1px solid ${(p) => p.$border};
  white-space: nowrap;
`;

const Muted = styled.span`
  color: var(--text-muted);
  font-size: var(--text-sm);
`;

const Note = styled.p`
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: 1.55;
  margin: 0.5rem 0 0;
`;

const Problem = styled.div`
  border: 1px solid rgba(248, 113, 113, 0.4);
  background: var(--error-dim);
  border-radius: var(--radius-md);
  padding: 0.7rem 0.9rem;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  line-height: 1.55;
  margin: 0.75rem 0 0;
`;

const Foot = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1.35rem;
  border-top: 1px solid var(--border-color);
`;

const Danger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.45rem 0.8rem;
  color: var(--text-muted);
  font-size: var(--text-sm);
  cursor: pointer;

  &:hover { color: var(--error-color); border-color: rgba(248, 113, 113, 0.5); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const SIGNAL_TONE = {
  bg: 'rgba(34,211,238,0.10)', fg: 'var(--accent-cyan)', border: 'rgba(34,211,238,0.32)'
};

/** The fields somebody can correct, in the order they are read. */
const FIELDS = [
  { key: 'fullName', label: 'Name' },
  { key: 'title', label: 'Title' },
  { key: 'organizationName', label: 'Company' },
  { key: 'domain', label: 'Website' },
  { key: 'location', label: 'Location' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Phone' },
  { key: 'linkedinUrl', label: 'LinkedIn' }
];

/** One click-to-edit row. */
function EditableRow({ field, value, onSave, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const input = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => { if (editing && input.current) input.current.focus(); }, [editing]);

  const commit = async () => {
    setEditing(false);
    if ((draft || '') === (value || '')) return;
    await onSave(field.key, draft);
  };

  return (
    <Row>
      <Key htmlFor={`f-${field.key}`}>{field.label}</Key>
      {editing ? (
        <Input
          id={`f-${field.key}`}
          ref={input}
          type={field.type || 'text'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            // Escape abandons the edit rather than saving whatever was typed.
            if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
          }}
        />
      ) : (
        <Value
          id={`f-${field.key}`}
          type="button"
          $empty={!value}
          disabled={disabled}
          onClick={() => setEditing(true)}
        >
          {value || 'Not on record'}
        </Value>
      )}
    </Row>
  );
}

function LeadDrawer({ lead, lists, onClose, onChanged, onDeleted }) {
  const [current, setCurrent] = useState(lead);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setCurrent(lead); setError(''); }, [lead]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!current) return null;

  const save = async (key, value) => {
    try {
      setError('');
      const updated = await prospectService.updateLead(current.id, { [key]: value });
      // The server returns the record it actually stored, which is how a
      // lower-cased address or a cleared status shows up here without guessing.
      const merged = {
        ...current,
        [key]: value || null,
        email: updated.email || null,
        emailStatus: updated.emailStatus || 'unavailable',
        emailProvider: updated.emailProvider || null
      };
      setCurrent(merged);
      if (onChanged) onChanged(merged);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not save that change');
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${current.fullName || 'this lead'} from Leads? This cannot be undone.`)) return;
    try {
      setBusy(true);
      setError('');
      await prospectService.deleteLead(current.id);
      if (onDeleted) onDeleted(current.id);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not remove that lead');
    } finally {
      setBusy(false);
    }
  };

  const emailTone = current.emailStatus === 'verified'
    ? { label: current.emailProvider === 'manual' ? 'added by you' : 'confirmed', bg: 'var(--success-dim)', fg: 'var(--success-color)', border: 'rgba(52,211,153,0.35)' }
    : current.emailStatus === 'risky'
      ? { label: 'unproven', bg: 'var(--warning-dim)', fg: 'var(--warning-color)', border: 'rgba(251,191,36,0.35)' }
      : null;

  const memberOf = (current.lists || [])
    .map((id) => (lists.find((l) => l._id === id) || {}).name)
    .filter(Boolean);

  return (
    <>
      <Backdrop onClick={onClose} />
      <Panel role="dialog" aria-label={`Details for ${current.fullName || 'this lead'}`}>
        <Head>
          <div style={{ minWidth: 0 }}>
            <Name>{current.fullName || 'Unknown'}</Name>
            <Sub>{[current.title, current.organizationName].filter(Boolean).join(' · ') || 'No role on record'}</Sub>
          </div>
          <IconButton onClick={onClose} aria-label="Close"><Icon name="close" size={17} /></IconButton>
        </Head>

        <Body>
          <Section>
            <SectionTitle><Icon name="edit" size={13} /> Details</SectionTitle>
            {FIELDS.map((field) => (
              <EditableRow key={field.key} field={field} value={current[field.key]} onSave={save} />
            ))}
            {emailTone && (
              <Row>
                <Key as="div">Address</Key>
                <div><Chip $bg={emailTone.bg} $fg={emailTone.fg} $border={emailTone.border}>{emailTone.label}</Chip></div>
              </Row>
            )}
            <Note>Click any value to change it. Each saves as you leave it.</Note>
            {error && <Problem>{error}</Problem>}
          </Section>

          <Section>
            <SectionTitle><Icon name="spark" size={13} /> Why they came up</SectionTitle>
            {current.signal ? (
              <>
                <Static style={current.signalStale ? { opacity: 0.6 } : undefined}>{current.signal}</Static>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
                  {current.signalType && <Chip {...{ $bg: SIGNAL_TONE.bg, $fg: SIGNAL_TONE.fg, $border: SIGNAL_TONE.border }}>{current.signalType}</Chip>}
                  {current.signalAt && <Muted>{new Date(current.signalAt).toLocaleDateString()}</Muted>}
                  {current.signalStale && <Muted>· old news</Muted>}
                  {current.signalUrl && (
                    <a href={current.signalUrl} target="_blank" rel="noreferrer noopener" style={{ fontSize: 'var(--text-sm)' }}>
                      source
                    </a>
                  )}
                </div>
              </>
            ) : <Muted>No signal recorded.</Muted>}
          </Section>

          {current.extra && Object.keys(current.extra).length > 0 && (
            <Section>
              <SectionTitle><Icon name="layers" size={13} /> From their file</SectionTitle>
              {Object.entries(current.extra).map(([key, value]) => (
                <Row key={key}>
                  <Key as="div" title={key}>{key}</Key>
                  <Static>{String(value)}</Static>
                </Row>
              ))}
            </Section>
          )}

          <Section>
            <SectionTitle><Icon name="info" size={13} /> Record</SectionTitle>
            <Row>
              <Key as="div">Lists</Key>
              <Static>{memberOf.length ? memberOf.join(', ') : <Muted>Not in any list</Muted>}</Static>
            </Row>
            <Row>
              <Key as="div">Source</Key>
              <Static>{current.source || <Muted>Unknown</Muted>}</Static>
            </Row>
            <Row>
              <Key as="div">Status</Key>
              <Static>
                {current.suppressed
                  ? <Chip $bg="var(--error-dim)" $fg="var(--error-color)" $border="rgba(248,113,113,0.35)">suppressed</Chip>
                  : current.engagement
                    ? String(current.engagement.state).replace(/_/g, ' ')
                    : <Muted>Not contacted</Muted>}
              </Static>
            </Row>
            <Row>
              <Key as="div">First seen</Key>
              <Static>{current.firstSeenAt ? new Date(current.firstSeenAt).toLocaleDateString() : <Muted>—</Muted>}</Static>
            </Row>
          </Section>
        </Body>

        <Foot>
          <Danger onClick={remove} disabled={busy}>
            <Icon name="trash" size={14} /> {busy ? 'Removing…' : 'Remove lead'}
          </Danger>
          {current.linkedinUrl && (
            <a href={current.linkedinUrl} target="_blank" rel="noreferrer noopener" style={{ fontSize: 'var(--text-sm)', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              LinkedIn <Icon name="external" size={13} />
            </a>
          )}
        </Foot>
      </Panel>
    </>
  );
}

export default LeadDrawer;
